import { FC, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { temAlgumRelatorio } from '@/config/relatorios';
import LoadingSpinner from './LoadingSpinner';

interface RelatoriosRouteProps {
  children: React.ReactNode;
}

/**
 * Guard da home de relatórios (`/relatorios`).
 *
 * A home é só um índice: quem tem QUALQUER uma das frentes liberada entra e vê os
 * relatórios que pode abrir. Quem não tem nenhuma não tem o que ver ali. Cada
 * relatório continua com o seu próprio guard (`DashboardRoute`, `SeopRoute`) —
 * este aqui não substitui nenhum deles, só evita mandar a pessoa para uma página
 * que estaria vazia.
 *
 * `undefined` nas flags conta como bloqueado, igual aos outros guards.
 */
const RelatoriosRoute: FC<RelatoriosRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const { showToast } = useToast();

  const blocked =
    !isLoading &&
    isAuthenticated &&
    !!user &&
    !temAlgumRelatorio({
      dashboard: user.dashboard_feature_enabled,
      seop: user.seop_feature_enabled,
    });

  useEffect(() => {
    if (blocked) {
      showToast('Os relatórios ainda não foram liberados pra você. Fale com seu admin.', 'warning');
    }
  }, [blocked, showToast]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (blocked) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default RelatoriosRoute;
